import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { revealFormErrors } from 'app/main/helpers/form-validation';

export type MoveCopyMode = 'move' | 'copy';

export interface MoveCopyModalCopyResult {
    destinationEntityIds: string[];
    year: number;
}

@Component({
    selector: 'app-move-copy-modal',
    templateUrl: './move-copy-modal.component.html',
    styleUrls: ['./move-copy-modal.component.scss'],
    standalone: false,
})
export class MoveCopyModalComponent implements OnInit {
    public mode: MoveCopyMode = 'move';

    // Shared: candidate destination questionnaires (expert mode, with the active questionnaire
    // filtered out in move mode so we don't move rows onto themselves).
    public questionnaires: QuestionnaireDto[] = [];

    // Shared: year drives the entity list in both modes.
    public availableYears: number[] = [];
    public filteredEntities: OrganizationEntityDto[] = [];

    // Move-mode picks a single entity; copy-mode picks many. The destination questionnaire ID
    // for move is derived from (selectedEntity, selectedYear) at submit time.
    public selectedEntity: OrganizationEntityDto | undefined;
    public selectedEntities: OrganizationEntityDto[] = [];

    private _selectedYear: number | null = null;
    private allEntities: OrganizationEntityDto[] = [];

    // The form and its element, to reveal what is missing on submit.
    @ViewChild('moveCopyForm') private moveCopyForm?: NgForm;
    @ViewChild('moveCopyForm', { read: ElementRef }) private moveCopyFormElement?: ElementRef<HTMLElement>;

    constructor(
        public dialogConfig: DynamicDialogConfig,
        public ref: DynamicDialogRef,
    ) {}

    ngOnInit(): void {
        const data = this.dialogConfig.data as {
            questionnaires: QuestionnaireDto[];
            option: MoveCopyMode;
            activeQuestionnaireId: string;
            entities: OrganizationEntityDto[];
        };
        this.mode = data.option;
        this.allEntities = data.entities ?? [];

        // Expert mode only — both flows ignore non-expert questionnaires.
        this.questionnaires = data.questionnaires.filter((quest) => quest.typeId === 2);

        // Move cannot land on the active questionnaire itself; copy can.
        if (this.mode === 'move') {
            this.questionnaires = this.questionnaires.filter((quest) => quest.id !== data.activeQuestionnaireId);
        }

        // Union of years across the remaining candidate questionnaires, sorted descending.
        const years = new Set<number>();
        for (const quest of this.questionnaires) {
            if (quest.refYear !== null && quest.refYear !== undefined) {
                years.add(quest.refYear);
            }
        }
        this.availableYears = Array.from(years).sort((a, b) => b - a);

        // Default the year to the source year when it's still a candidate.
        const sourceQuestionnaire = data.questionnaires.find((quest) => quest.id === data.activeQuestionnaireId);
        const sourceYear = sourceQuestionnaire?.refYear ?? null;
        this._selectedYear = sourceYear !== null && this.availableYears.includes(sourceYear) ? sourceYear : null;
        this.refilterEntitiesForYear();
    }

    public get selectedYear(): number | null {
        return this._selectedYear;
    }

    public set selectedYear(value: number | null) {
        this._selectedYear = value;
        this.refilterEntitiesForYear();
    }

    public onSubmit(): void {
        if (
            this.moveCopyForm &&
            this.moveCopyFormElement &&
            !revealFormErrors(this.moveCopyForm.form, this.moveCopyFormElement.nativeElement)
        ) {
            return;
        }
        if (this._selectedYear === null) {
            return;
        }

        if (this.mode === 'copy') {
            if (!this.selectedEntities.length) {
                return;
            }
            const result: MoveCopyModalCopyResult = {
                destinationEntityIds: this.selectedEntities.map((entity) => entity.id ?? '').filter((id) => !!id),
                year: this._selectedYear,
            };
            this.ref.close(result);
            return;
        }

        // Move: resolve (entity, year) -> the single matching questionnaire ID.
        if (!this.selectedEntity) {
            return;
        }
        const targetQuestionnaire = this.questionnaires.find(
            (quest) => quest.organizationEntityId === this.selectedEntity?.id && quest.refYear === this._selectedYear,
        );
        if (!targetQuestionnaire?.id) {
            return;
        }
        this.ref.close(targetQuestionnaire.id);
    }

    public onCancel(): void {
        this.ref.close();
    }

    /**
     * Recompute filteredEntities = entities that have a candidate expert questionnaire for the
     * currently-selected year. Also drops previously-selected entries that no longer match.
     */
    private refilterEntitiesForYear(): void {
        if (this._selectedYear === null) {
            this.filteredEntities = [];
            this.selectedEntity = undefined;
            this.selectedEntities = [];
            return;
        }
        const eligibleIds = new Set<string>(
            this.questionnaires
                .filter((quest) => quest.refYear === this._selectedYear)
                .map((quest) => quest.organizationEntityId ?? ''),
        );
        this.filteredEntities = this.allEntities.filter((entity) => eligibleIds.has(entity.id ?? ''));
        const eligibleEntityIds = new Set<string>(this.filteredEntities.map((entity) => entity.id ?? ''));
        if (this.selectedEntity && !eligibleEntityIds.has(this.selectedEntity.id ?? '')) {
            this.selectedEntity = undefined;
        }
        this.selectedEntities = this.selectedEntities.filter((entity) => eligibleEntityIds.has(entity.id ?? ''));
    }
}
